import { ArrowDownRight, MapPin } from "lucide-react";
import { cafeInfo } from "@/data/menu";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/6ef962f5-2a78-4560-a002-cc5704aa1098/images/6667a7ec04e7997ed1e0eaa030f9de22857b36fc9766b0c5733e19974d713216.png";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-cream pt-28 md:pt-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-16 md:grid-cols-12 md:px-10 md:pb-24">
        {/* Copy */}
        <div className="md:col-span-6 flex flex-col justify-center">
          <span
            data-testid="hero-eyebrow"
            className="eyebrow animate-fade-up"
          >
            Café · Köln · Chlodwigplatz
          </span>

          <h1
            data-testid="hero-headline"
            className="font-display mt-6 text-[18vw] leading-[0.9] tracking-tighter text-brand md:text-[7.5vw] lg:text-[6.5vw] xl:text-[6vw] animate-fade-up delay-100"
          >
            Scoops
            <span className="text-matcha">&</span>Co
          </h1>

          <p
            data-testid="hero-slogan"
            className="font-display mt-6 text-3xl font-medium italic text-text_secondary md:text-4xl animate-fade-up delay-200"
            style={{ color: "#5c5752" }}
          >
            — Made to Crave.
          </p>

          <p
            data-testid="hero-description"
            className="mt-8 max-w-md text-base leading-relaxed text-[#5c5752] md:text-lg animate-fade-up delay-300"
          >
            Italienisches Eis, zeremonieller Matcha, frische Cookies, Specialty
            Coffee und Açaí Bowls. Ein Ort am Chlodwigplatz, der Genuss neu
            definiert.
          </p>

          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up delay-500">
            <a
              href="#menu"
              data-testid="hero-cta-menu"
              className="btn-primary"
              onClick={(e) => {
                e.preventDefault();
                document
                  .querySelector("#menu")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Menü entdecken
              <ArrowDownRight size={18} />
            </a>
            <a
              href="#standort"
              data-testid="hero-cta-location"
              className="btn-ghost"
              onClick={(e) => {
                e.preventDefault();
                document
                  .querySelector("#standort")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <MapPin size={18} />
              Besuch uns
            </a>
          </div>

          {/* Inline meta */}
          <div className="mt-14 grid grid-cols-2 gap-6 max-w-md animate-fade-up delay-700">
            <div>
              <p className="eyebrow">Adresse</p>
              <p className="mt-2 text-sm font-medium text-brand">
                {cafeInfo.address.street}
                <br />
                {cafeInfo.address.zip} {cafeInfo.address.city}
              </p>
            </div>
            <div>
              <p className="eyebrow">Heute geöffnet</p>
              <p className="mt-2 text-sm font-medium text-brand">
                {(() => {
                  // 0 = Sonntag, 1-4 = Mo-Do, 5-6 = Fr-Sa
                  const d = new Date().getDay();
                  if (d === 0) return "12:00 – 22:00";
                  if (d === 5 || d === 6) return "11:00 – 23:00";
                  return "11:00 – 22:00";
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="md:col-span-6 relative">
          <div
            className="zoom-hover relative aspect-[4/5] w-full overflow-hidden rounded-[28px] border border-warm animate-fade-in delay-300"
          >
            <img
              src={HERO_IMAGE}
              alt="Matcha Latte, Cookie Sandwich & Schokoladencookie auf Marmor"
              className="h-full w-full object-cover"
              loading="eager"
            />
            {/* Floating tag */}
            <div
              data-testid="hero-floating-tag"
              className="absolute bottom-6 left-6 right-6 md:left-8 md:right-auto md:max-w-[260px] rounded-2xl bg-white/95 p-5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)]"
            >
              <p className="eyebrow">Signature</p>
              <p className="font-display mt-2 text-2xl font-bold leading-tight text-brand">
                Cookie Sandwich
              </p>
              <p className="mt-1 text-sm text-[#5c5752]">
                Warm cookie · house-made ice cream
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="border-y border-warm bg-white overflow-hidden">
        <div className="animate-marquee whitespace-nowrap py-5 flex gap-12">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-12 px-6">
              {[
                "Matcha",
                "Hojicha",
                "Ice Cream Sandwiches",
                "Açaí Bowls",
                "Specialty Coffee",
                "Brownies",
                "Cloud Drinks",
                "Cookies",
              ].map((label) => (
                <span
                  key={`${i}-${label}`}
                  className="font-display text-2xl md:text-3xl font-medium uppercase tracking-tight text-brand"
                >
                  {label}
                  <span className="text-matcha"> · </span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
